import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

const MAX_TABS = 10;
const STORAGE_KEY = 'workspace-tabs';

export type WorkspaceTabType = 'client' | 'advance';

export interface WorkspaceTab {
  id: string;
  type: WorkspaceTabType;
  entityId: string;
  label: string;
  route: string;
  openedAt: number;
  lastViewedAt: number;
}

type OpenTabInput = Omit<WorkspaceTab, 'openedAt' | 'lastViewedAt'>;

interface WorkspaceTabsState {
  tabs: WorkspaceTab[];
  activeTabId: string | null;
  openTab: (tab: OpenTabInput) => void;
  closeTab: (id: string) => { nextRoute: string };
  activate: (id: string) => void;
  updateRoute: (id: string, route: string) => void;
  updateLabel: (id: string, label: string) => void;
  clearAll: () => void;
}

export const useWorkspaceTabsStore = create<WorkspaceTabsState>()(
  persist(
    (set, get) => ({
      tabs: [],
      activeTabId: null,

      openTab: incoming => {
        const now = Date.now();
        const { tabs } = get();
        const existing = tabs.find(t => t.id === incoming.id);

        let nextTabs: WorkspaceTab[];
        if (existing) {
          nextTabs = tabs.map(t =>
            t.id === incoming.id
              ? {
                  ...t,
                  label: incoming.label,
                  route: incoming.route,
                  lastViewedAt: now,
                }
              : t
          );
        } else {
          const created: WorkspaceTab = {
            ...incoming,
            openedAt: now,
            lastViewedAt: now,
          };
          nextTabs = [...tabs, created];

          if (nextTabs.length > MAX_TABS) {
            // Evict the oldest (by lastViewedAt) tab other than the one just opened.
            const evictable = nextTabs
              .filter(t => t.id !== incoming.id)
              .sort((a, b) => a.lastViewedAt - b.lastViewedAt);
            const victim = evictable[0];
            if (victim) {
              nextTabs = nextTabs.filter(t => t.id !== victim.id);
            }
          }
        }

        set({ tabs: nextTabs, activeTabId: incoming.id });
      },

      closeTab: id => {
        const { tabs, activeTabId } = get();
        const idx = tabs.findIndex(t => t.id === id);
        if (idx === -1) return { nextRoute: '/clients' };

        const nextTabs = tabs.filter(t => t.id !== id);
        // Prefer the tab that shifts into the closed slot (right neighbor), else left.
        const neighbor = nextTabs[idx] ?? nextTabs[idx - 1] ?? null;
        const nextRoute = neighbor?.route ?? '/clients';

        const nextActiveId =
          activeTabId === id ? (neighbor?.id ?? null) : activeTabId;

        set({ tabs: nextTabs, activeTabId: nextActiveId });
        return { nextRoute };
      },

      activate: id => {
        const { tabs } = get();
        if (!tabs.some(t => t.id === id)) return;
        const now = Date.now();
        set({
          activeTabId: id,
          tabs: tabs.map(t => (t.id === id ? { ...t, lastViewedAt: now } : t)),
        });
      },

      updateRoute: (id, route) => {
        set(state => ({
          tabs: state.tabs.map(t => (t.id === id ? { ...t, route } : t)),
        }));
      },

      updateLabel: (id, label) => {
        set(state => ({
          tabs: state.tabs.map(t => (t.id === id ? { ...t, label } : t)),
        }));
      },

      clearAll: () => set({ tabs: [], activeTabId: null }),
    }),
    {
      name: STORAGE_KEY,
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        tabs: state.tabs,
        activeTabId: state.activeTabId,
      }),
    }
  )
);

export function buildClientTabId(userId: string): string {
  return `client:${userId}`;
}

export function buildClientTabLabel(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  fallbackId?: string
): string {
  const name = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (name) return `Client - ${name}`;
  if (fallbackId) return `Client - ${fallbackId.slice(0, 6)}…`;
  return 'Client';
}

export function buildAdvanceTabId(loanId: string): string {
  return `advance:${loanId}`;
}

/**
 * "Advance 9990001-3 - Ada Lovelace": the loanNumber (`clientNumber-draw`) is
 * the reference staff quote, so it leads when known. Falls back to the draw
 * while the loan is still loading, then to a UUID prefix.
 */
export function buildAdvanceTabLabel({
  loanNumber,
  draw,
  clientName,
  loanId,
}: {
  loanNumber?: string | null;
  draw: number | null | undefined;
  clientName?: string | null;
  loanId?: string;
}): string {
  const number = loanNumber?.trim();
  const advancePart = number
    ? `Advance ${number}`
    : typeof draw === 'number' && Number.isFinite(draw)
      ? `Advance #${draw}`
      : loanId
        ? `Advance - ${loanId.slice(0, 6)}…`
        : 'Advance';
  const namePart = clientName?.trim();
  return namePart ? `${advancePart} - ${namePart}` : advancePart;
}
